/**
 * Utilitário para pré-carregar imagens de produtos
 * Isso melhora significativamente a experiência do usuário ao navegar pelos produtos
 */

import { getImageUrl } from './imageUtils';

// Cache para controlar quais imagens já foram pré-carregadas
const preloadedImages = new Set();

// Limite de imagens em cache para evitar uso excessivo de memória
const MAX_CACHE_SIZE = 200;

/**
 * Limpa o cache de imagens pré-carregadas quando excede o limite
 */
const limparCacheSeNecessario = () => {
  if (preloadedImages.size > MAX_CACHE_SIZE) {
    // Converter para array, remover os primeiros itens e criar novo Set
    const imageArray = Array.from(preloadedImages);
    const novoArray = imageArray.slice(imageArray.length / 2);
    preloadedImages.clear();
    novoArray.forEach(img => preloadedImages.add(img));
    console.log(`Cache de imagens reduzido de ${imageArray.length} para ${novoArray.length} itens`);
  }
};

/**
 * Pré-carrega uma imagem em segundo plano
 * @param {string} imagePath - Caminho da imagem a ser pré-carregada
 * @param {number} width - Largura desejada da imagem
 * @param {Function} callback - Função de callback opcional chamada quando a imagem estiver carregada
 * @returns {Promise} - Promise que resolve quando a imagem estiver carregada
 */
export const preloadImage = (imagePath, width = 300, callback = null) => {
  if (!imagePath) return Promise.reject(new Error('Caminho de imagem inválido'));
  if (preloadedImages.has(imagePath)) return Promise.resolve(imagePath);
  
  const url = getImageUrl(imagePath, width);
  
  return new Promise((resolve, reject) => {
    // Criar um objeto Image para carregar a imagem em segundo plano
    const img = new Image();
    
    img.onload = () => {
      // Marcar como pré-carregada
      preloadedImages.add(imagePath);
      if (callback && typeof callback === 'function') callback(url);
      resolve(url);
    };
    
    img.onerror = () => {
      reject(new Error(`Falha ao carregar imagem: ${url}`));
    };
    
    img.src = url;
  });
};

/**
 * Pré-carrega imagens de uma lista de produtos
 * @param {Array} produtos - Lista de produtos
 * @param {number} quantidade - Quantidade máxima de imagens a pré-carregar
 * @param {number} prioridade - Número de imagens para carregar com alta prioridade
 * @returns {Promise} - Promise que resolve quando todas as imagens prioritárias estiverem carregadas
 */
export const preloadProductImages = (produtos, quantidade = 10, prioridade = 4) => {
  if (!produtos || !Array.isArray(produtos)) return Promise.resolve([]);
  
  // Limitar a quantidade para não sobrecarregar
  const produtosParaPreload = produtos.slice(0, quantidade);
  
  // Separar em imagens prioritárias e não prioritárias
  const prioritarias = produtosParaPreload.slice(0, prioridade);
  const restantes = produtosParaPreload.slice(prioridade);
  
  // Carregar imagens prioritárias imediatamente
  const promessasPrioritarias = prioritarias.map(produto => {
    if (produto && produto.code) {
      return preloadImage(`/imagens/${produto.code}.png`, 300);
    }
    return Promise.resolve();
  });
  
  // Carregar o restante em segundo plano
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => {
      restantes.forEach(produto => {
        if (produto && produto.code) {
          preloadImage(`/imagens/${produto.code}.png`);
        }
      });
    });
  } else {
    // Fallback para navegadores que não suportam requestIdleCallback
    setTimeout(() => {
      restantes.forEach(produto => {
        if (produto && produto.code) {
          preloadImage(`/imagens/${produto.code}.png`);
        }
      });
    }, 500); // Reduzido para 500ms para ser mais rápido
  }
  
  // Retornar promise que resolve quando todas as imagens prioritárias estiverem carregadas
  return Promise.all(promessasPrioritarias);
};

export default {
  preloadImage,
  preloadProductImages
};
